import { type FC } from 'react'

import { match } from 'ts-pattern'

import {
  GreyGrey20,
  GreyGrey60,
  GreyGrey70,
  GreyGrey80,
  YellowYellow10,
  YellowYellow80
} from '@sequencehq/core-components'

import { NodeType } from 'Automations/nodeDefinitions/types'
import {
  IF_LABEL,
  ELSE_HANDLE_LABEL
} from 'Automations/nodeDefinitions/definitions/switch/switch.constants'
import {
  APPROVED_HANDLE_LABEL,
  REJECTED_HANDLE_LABEL
} from 'Automations/nodeDefinitions/definitions/review/review.constants'

/**
 * This component is rendered via `createRoot` into a detached DOM overlay
 * element (see useDragWithMorph), outside the main React tree and
 * ChakraProvider. Raw HTML elements are used intentionally because:
 *
 * 1. Chakra components need a ChakraProvider ancestor for theming/Emotion —
 *    without one, they inject styles independently and slower.
 * 2. All initial styles are immediately overwritten by direct DOM
 *    manipulation in the dragover handler (useDragWithMorph), so Chakra's
 *    style props would be discarded on first frame anyway.
 * 3. Keeping this tree lightweight avoids unnecessary Emotion setup cost.
 */

const NOTE_PLACEHOLDER = 'rgba(146, 64, 14, 0.5)'

const getMorphRows = (nodeType: NodeType): { text: string }[] =>
  match(nodeType)
    .with('SWITCH', () => [{ text: IF_LABEL }, { text: ELSE_HANDLE_LABEL }])
    .with('REVIEW_PLACEHOLDER', () => [
      { text: APPROVED_HANDLE_LABEL },
      { text: REJECTED_HANDLE_LABEL }
    ])
    .otherwise(() => [])

const NoteMorphPreview: FC<{
  renderIcon: () => React.ReactElement
}> = ({ renderIcon }) => {
  return (
    <div
      data-morph-container
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '0px',
        borderRadius: '6px',
        border: 'transparent 1px solid'
      }}
    >
      <div
        data-morph-icon
        style={{ flexShrink: 0, transformOrigin: 'top left' }}
      >
        {renderIcon()}
      </div>
      <div
        data-morph-body
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          opacity: 0,
          maxHeight: '0px',
          overflow: 'hidden'
        }}
      >
        <span
          style={{
            fontSize: '13px',
            fontWeight: 600,
            lineHeight: '20px',
            color: YellowYellow80
          }}
        >
          Note
        </span>
        <span
          style={{
            fontSize: '13px',
            lineHeight: '20px',
            color: NOTE_PLACEHOLDER
          }}
        >
          Add text
        </span>
      </div>
    </div>
  )
}

const StandardMorphPreview: FC<{
  renderIcon: () => React.ReactElement
  nodeType: NodeType
  name: string
  description?: string
}> = ({ renderIcon, nodeType, name, description }) => {
  const rows = getMorphRows(nodeType)

  return (
    <div
      data-morph-container
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '0px',
        borderRadius: '6px',
        outline: 'transparent 1px solid'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div
          data-morph-icon
          style={{ flexShrink: 0, transformOrigin: 'center' }}
        >
          {renderIcon()}
        </div>
        <div
          data-morph-label
          style={{
            opacity: 0,
            maxWidth: '0px',
            overflow: 'hidden',
            whiteSpace: 'nowrap'
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              paddingLeft: '8px'
            }}
          >
            <span
              style={{
                fontSize: '13px',
                fontWeight: 600,
                lineHeight: '20px',
                color: GreyGrey80
              }}
            >
              {name}
            </span>
            {description && (
              <span
                style={{
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: GreyGrey60
                }}
              >
                {description}
              </span>
            )}
          </div>
        </div>
      </div>
      {rows.length > 0 && (
        <div
          data-morph-body
          style={{
            opacity: 0,
            maxHeight: '0px',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              paddingTop: '8px'
            }}
          >
            {rows.map(row => (
              <div
                key={row.text}
                style={{
                  background: GreyGrey20,
                  borderRadius: '6px',
                  padding: '8px',
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}
              >
                <span
                  style={{
                    fontSize: '13px',
                    lineHeight: '20px',
                    color: GreyGrey70
                  }}
                >
                  {row.text}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export const DragMorphPreview: FC<{
  renderIcon: () => React.ReactElement
  nodeType: NodeType
  name: string
  description?: string
}> = ({ renderIcon, nodeType, name, description }) => {
  if (nodeType === 'NOTE') {
    return <NoteMorphPreview renderIcon={renderIcon} />
  }

  return (
    <StandardMorphPreview
      renderIcon={renderIcon}
      nodeType={nodeType}
      name={name}
      description={description}
    />
  )
}
